import React, {useState, useEffect, useContext} from 'react';
import { useTranslation } from 'react-i18next';
import { BreakdownsContext } from "./contexts/BreakdownsContext";
import { PieChart, PieChartDatum } from '../../../components/PieChart/PieChart';
import { BreakdownsContextType } from "./contexts/BreakdownsContextTypes";
import { BreakdownsContainer } from '../../../components/BreakdownsContainer/BreakdownsContainer';
import { formatWithCustomGigaBillion } from '../../../utils/fomatNumbers';
import { mapAffiliationEntityTypes } from '../../../utils/actors';
import { AFFILIATION_TYPE_VALUES, AffiliationType, normalizeAffiliationType } from '../../../utils/affiliationTypes';


interface StateAffiliatedChartProps {
  narrative: any;
  mode?: 'actors' | 'publications';
  isClickable?: boolean;
  isShare?: boolean;
};

type ChartData = ChartDataItem[];

interface ChartDataItem {
  affiliation_type: string | null;
  actors_count: number;
  actors_share: number;
  publications_count: number;
  publications_share: number;
}

/**
 * The backend still reports the legacy enum (e.g. STATE_AGENCY and
 * GOVERNMENT_OFFICIAL as separate rows); fold rows into the new categories,
 * summing their counts. `null` ("Other") and unknown values are kept as-is.
 */
const foldAffiliationDistribution = (items: ChartDataItem[]): ChartDataItem[] => {
  const folded = new Map<string | null, ChartDataItem>();
  items.forEach((item) => {
    const key = item.affiliation_type === null
      ? null
      : normalizeAffiliationType(item.affiliation_type) ?? item.affiliation_type;
    const existing = folded.get(key);
    if (existing) {
      existing.actors_count += item.actors_count;
      existing.actors_share += item.actors_share;
      existing.publications_count += item.publications_count;
      existing.publications_share += item.publications_share;
    } else {
      folded.set(key, { ...item, affiliation_type: key });
    }
  });
  return Array.from(folded.values());
};

interface StateAffiliatedData {
    total_audience: number;
    affiliation_distribution: Array<{
      affiliation_type: string;
      actors_count: number;
      actors_share: number;
      publications_count: number;
      publications_share: number;
    }>;
    top_actors: Array<{
      id: number;
      name: string;
      affiliation_type: string;
      country_name: string;
    }>;
    top_countries: Array<{
      id: number;
      name: string;
      actors_count: number;
      publications_count: number;
    }>;
    other_countries: {
      id: number;
      name: string;
      actors_count: number;
      publications_count: number;
    };
  };

export const StateAffiliatedChart: React.FC<StateAffiliatedChartProps> = ({
  mode='actors',
  isClickable = true,
  isShare = false,
}) => {
  const { t } = useTranslation();
  const [chartData, setChartData] = useState<ChartData | null>(null);
  const [actorsData, setActorsData] = useState<PieChartDatum[] | null>(null);
  const [publicationsData, setPublicationsData] = useState<PieChartDatum[] | null>(null);
  const [currentMode, setCurrentMode] = useState<'actors' | 'publications'>(mode);
  const { stateAffiliated, openModal, setTypesFilter, setAffiliationTypesFilter, setCurrentWidgetName, setAffiliationTypesExcludeFilter, setModalTitle } = useContext(BreakdownsContext) as BreakdownsContextType;

  const mapEntitiesToColors: Record<AffiliationType, string> = {
    OFFICIAL_STATE_CHANNELS: '#FFC936',
    STATE_CONTROLLED_OUTLETS: '#FF3636',
    STATE_LINKED_CHANNELS: '#A267DC',
    STATE_ALIGNED_CHANNELS: '#2EB8A8',
  }

  const getSliceColor = (item: ChartDataItem) =>
    item.affiliation_type === null
      ? '#306DCF'
      : mapEntitiesToColors[item.affiliation_type as AffiliationType];

  const getChartDataByMode = (mode: 'actors' | 'publications'): PieChartDatum[] => {
    if (!chartData) {
      return [];
    }

    const affiliationTypes = mapAffiliationEntityTypes(t);
    return chartData.map((item) => {
      const typeName = item.affiliation_type === null ? t('Other') : affiliationTypes[item.affiliation_type as keyof typeof affiliationTypes];
      const countMode = mode === 'actors' ? item.actors_count : item.publications_count;

      return {
        name: typeName,
        value: countMode,
        formattedCount: formatWithCustomGigaBillion(countMode),
        color: getSliceColor(item),
      };
    });
  };

  useEffect(() => {
    if (!stateAffiliated) {
      return;
    }

    setChartData(
      stateAffiliated?.affiliation_distribution
        ? foldAffiliationDistribution(stateAffiliated.affiliation_distribution)
        : null,
    );
  }, [stateAffiliated]);

  useEffect(() => {
    if (chartData) {
      setActorsData( getChartDataByMode('actors'));
      setPublicationsData( getChartDataByMode('publications'));
    }
  }, [chartData]);


  const audienceCount = formatWithCustomGigaBillion(stateAffiliated?.total_audience || 0);
  const chartBadge = currentMode === 'actors' ? `${t('State-affiliated audience:')} ${audienceCount}` : '';

  const breakdownsContainerProps: any = {
    title: t('State-affiliated distribution'),
    onTabChange: (tabValue: 'actors' | 'publications') => {
      setCurrentMode(tabValue);
    },
    tabs: [
      { name: t('Actors'), value: 'actors' },
      { name: t('Publications'), value: 'publications' },
    ],
    flexGrow: 2,
  };

  return (
    <BreakdownsContainer {...breakdownsContainerProps}>
      <PieChart
        chartData={currentMode === 'actors' ? actorsData : publicationsData}
        isClickable={isClickable}
        onSelect={(index) => {
          if ( index === null || !stateAffiliated) return;

          const selectedAffiliationType = chartData ? chartData[index]?.affiliation_type : null;
    
          const affiliationTypes = mapAffiliationEntityTypes(t);
          if (selectedAffiliationType === null) {
            setAffiliationTypesFilter([...AFFILIATION_TYPE_VALUES]);
            setAffiliationTypesExcludeFilter(true);
            setModalTitle(t('Other'));
          } else {
            setAffiliationTypesFilter([selectedAffiliationType]);
            setTypesFilter({value: 'state_affiliated', label: 'State-affiliated', bool: true})
            setModalTitle(affiliationTypes[selectedAffiliationType as keyof typeof affiliationTypes]);
          }
          setCurrentWidgetName('stateAffiliatedTypesWidget');
          openModal();
          
        }}
        legendConfig={{
          position: 'bottom',
          textStyle: { fontSize: 14 },
        }}
        seriesName={currentMode === 'actors' ? t('Actors') : t('Publications')}
        chartBadge={chartBadge}
      />
      </BreakdownsContainer>
  );
};